import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatNumber, formatTime } from "@/shared/lib/i18n/formatters.ts";
import MachinesLiveChart from "@/machines/components/charts/machines-live-chart/machines-live-chart";
import { TruckEventOverview } from "@/machines/types/event.ts";
import { Machine } from "@/machines/types/machine.ts";
import { Settings } from "lucide-react";
import { useModal } from "@/shared/lib/modal/modal-context";

interface MachinesLiveMainProps {
  chartData: TruckEventOverview["graphData"];
  metadata: TruckEventOverview["metadata"];
  machine?: Machine;
  width?: number;
  height?: number;
  onBarClick?: (index: number) => void;
  activeIndex?: number | null;
}

const MachinesLiveMain = ({ chartData, metadata, machine, width = 1200, height = 500, onBarClick, activeIndex }: MachinesLiveMainProps) => {
  const { t } = useTranslation("machines");
  const totalPoints = 200;
  const [windowData, setWindowData] = useState(() => transformChartData(chartData, t).slice(-totalPoints));
  const storageKey = "machinesLiveMainHeight";
  const [containerHeight, setContainerHeight] = useState<number>(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(storageKey) : null;
    return stored ? Number(stored) : height;
  });

  const isDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  useEffect(() => {
    // If parent changes initial height prop, sync when not dragging
    if (!isDraggingRef.current) {
      setContainerHeight((prev) => (prev === height ? prev : height));
    }
     
  }, [height]);

  useEffect(() => {
    setWindowData(transformChartData(chartData, t).slice(-totalPoints));
  }, [chartData, t]);

  const onMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    const dy = e.clientY - startYRef.current;
    const next = Math.min(800, Math.max(180, startHeightRef.current + dy));
    setContainerHeight(next);
  };

  const onMouseUp = () => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    try {
      window.localStorage.setItem(storageKey, String(containerHeight));
    } catch {}
    window.removeEventListener("mousemove", onMouseMove as any);
    window.removeEventListener("mouseup", onMouseUp);
  };

  const startDrag = (clientY: number) => {
    isDraggingRef.current = true;
    startYRef.current = clientY;
    startHeightRef.current = containerHeight;
    window.addEventListener("mousemove", onMouseMove as any);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onHandleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    startDrag(e.clientY);
  };

  const onHandleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    startDrag(touch.clientY);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onMouseMove as any);
      window.removeEventListener("mouseup", onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { openModal } = useModal();

  const handleOpenTargets = () => {
    if (!machine) return;
    openModal("machine-targets", {
      machineId: machine.id,
      machineName: machine.name,
      loading_time_target_s: machine.loading_time_target_s,
      waiting_time_target_s: machine.waiting_time_target_s,
      buckets_per_truck_target: machine.buckets_per_truck_target,
    });
  };

  return (
    <div className="overflow-hidden w-full">
      <div className="bg-sidebar p-4 rounded-xl w-full overflow-x-auto border border-border">
        {machine && (
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-5 h-0.5 bg-orange-500" style={{ borderTop: "2px dashed orange" }} />
                <span>{t("live.legend.target", { value: machine.loading_time_target_s ?? "—" })}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-5 h-0.5 bg-blue-500" />
                <span>{t("live.legend.waitTarget", { value: machine.waiting_time_target_s ?? "—" })}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-green-500" />
                <span>{t("live.legend.bucketsPerTruck", { value: machine.buckets_per_truck_target ?? "—" })}</span>
              </div>
            </div>
            <button
              onClick={handleOpenTargets}
              className="p-1.5 rounded-md hover:bg-accent/50 transition-colors text-muted-foreground hover:text-foreground"
              title={t("live.editTargets")}
            >
              <Settings className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="relative w-full select-none" >
          <MachinesLiveChart
            targetTime={metadata.truckLoading.targetCycleTime}
            averageTime={metadata.truckLoading.avgCycleTime}
            data={windowData}
            width={width}
            height={containerHeight}
            onBarClick={onBarClick ? (_data, index) => onBarClick(index) : undefined}
            activeIndex={activeIndex}
          />
          <div
            className="absolute bottom-0 left-0 right-0 h-2 cursor-row-resize flex items-center justify-center"
            onMouseDown={onHandleMouseDown}
            onTouchStart={onHandleTouchStart}
          >
            <div className="w-10 h-1 rounded bg-border" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachinesLiveMain;

const transformChartData = (
  data: TruckEventOverview["graphData"],
  t: TFunction<"machines">,
) => {
  return data.map((item) => {
    const { truckLoading, waitingForTruck } = item;
    const cycleTime = +(truckLoading?.durationSeconds / 60).toFixed(1);
    const waitTimeValue = +(waitingForTruck?.durationSeconds / 60).toFixed(1);

    let status: "normal" | "warning" | "critical" = "normal";
    if (truckLoading.color === "#FFFF00") status = "warning";
    if (truckLoading.color === "#FF0009") status = "critical";

    const bouldersPresent = truckLoading.color === "#800080";
    const waitTimeText = t("units.minutes", {
      value: formatNumber(waitTimeValue, { maximumFractionDigits: 1 }),
    });

    const time = formatTime(new Date(truckLoading.startTime));

    // Get bucket count directly from the API data
    const bucketCount = truckLoading.bucketCount || 0;

    return {
      time,
      cycleTime,
      waitTimeValue,
      status,
      bucketCount,
      bouldersPresent,
      waitTimeText,
    };
  });
};
